import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import breakpoints from 'utils/breakpoints';
import styled from 'styled-components';
import { IconButton } from 'components/components/atoms';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import HeartIcon from 'components/icons/Heart';
import ShareIcon from 'components/icons/Share';
import MailIcon from 'components/icons/Mail';
import ChatIcon from 'components/icons/ChatBubble';
import tw from 'twin.macro';
import { getMobileOS } from 'utils/getMobileOS';
import { shareThis, getUrl } from 'utils/shareThis';
import getEmbedUrl from 'utils/getEmbedUrl';
import HeartFilled from 'components/icons/HeartFilled';
import { useSitecoreContext } from 'stores/sitecoreContext';
import getVideoType from 'utils/getVideoType';
import pauseVideos from 'utils/pauseVideos';
import { track } from 'utils/tracking';
import trackingEvents from 'utils/trackingEvents';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import { getScrFromIframe } from 'utils/getScrFromIframe';
import VideoIconGallery from 'components/icons/VideoIconGallery';
import TourIconGallery from 'components/icons/TourIconGallery';
import PhotoIconGallery from 'components/icons/PhotoIconGallery';
import { isValidUrl } from 'utils/validateUrlSrc';
// import LazyLoad from "react-lazyload";

const Container = styled.div`
  @media ${breakpoints.md} {
    max-height: 40.6rem;
  }
`;

const ImgContainer = styled.img`
  height: 19.5rem;
  @media ${breakpoints.md} {
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    height: 40.6rem;
  }
`;

const Iframe = styled.iframe`
  height: 19.5rem;
  @media ${breakpoints.md} {
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    height: 40.6rem;
  }
`;

const IframeContainer = styled.div`
  & iframe {
    ${tw`w-full`}
  }
`;
const InternalVideoContainer = styled.div`
  height: 19.5rem;
  @media ${breakpoints.md} {
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    height: 40.6rem;
  }
`;

const InternalVideo = styled.video`
  width: 100% !important;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const IconContainer = styled.div`
  width: 2.25rem;
  height: 2.25rem;
`;

const FiltersContainer = styled.div`
  height: 2.25rem;
`;

const AvailableFlag = styled.div`
  @media ${breakpoints.md} {
    height: 3.75rem;
  }
`;
const QMIFlagMessageContainer = styled.div`
  border-top-right-radius: 1.813rem;
  border-bottom-right-radius: 1.813rem;
  width: fit-content;
  border-top-width: 0.125rem;
  border-bottom-width: 0.125rem;
  border-right-width: 0.125rem;
  height: 2.25rem;
`;

const MessageFlagLogoContainer = styled.div`
  @media ${breakpoints.md} {
    margin-bottom: 6.75rem;
  }
  @media ${breakpoints.lg} {
    margin-bottom: 8.5rem;
  }
  @media ${breakpoints.xl} {
    margin-bottom: 1rem;
  }
`;
const MessageFlagLogoImage = styled.img`
  height: 2.2rem;
`;

const FiltersMediaButton = styled.button`
  ${({ isTour }) => isTour && 'width: 7.15rem;'}
`;

const isFilterSelected = (item, filter) =>
  (item.text === 'Videos' && filter === 'video') ||
  (item.text === '360 Tours' && filter === 'tour') ||
  (item.text === 'Photos' && filter === 'image');
const CustomArrowContainer = styled.div`
  &:before {
    display: none;
  }
  z-index: 2;
`;
const CustomNextArrow = (props, isMobile) => {
  const { className, style, onClick } = props;
  return (
    <CustomArrowContainer
      className={cx('mr-5 md:mr-10', className)}
      style={{ ...style }}
      onClick={onClick}
    >
      <IconButton
        className={cx(
          'absolute right-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 flex mr-5 w-10 h-10 md:w-16 md:h-16 md:mr-8',
        )}
        icon='arrow-right'
        idIcon={'MultiblockGallery'}
        color='white'
        circled
        shadowed
        hoverState
        buttonLabel='Next slide of Multiblock'
        small={isMobile}
        title={'Right arrow icon'}
      />
    </CustomArrowContainer>
  );
};
const CustomPrevArrow = (props, isMobile) => {
  const { className, style, onClick } = props;
  return (
    <CustomArrowContainer
      className={cx('ml-5 md:ml-10', className)}
      style={{ ...style }}
      onClick={onClick}
    >
      <IconButton
        className='absolute left-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200
          flex ml-5 sm:w-10 sm:h-10 md:w-16 md:h-16 md:ml-8'
        icon='arrow-left'
        idIcon={'MultiblockGallery'}
        color='white'
        circled
        shadowed
        hoverState
        buttonLabel='Previous slide on Multiblock'
        small={isMobile}
        title={'Left arrow icon'}
      />
    </CustomArrowContainer>
  );
};
const ExtendedGallery = ({
  className,
  carousel,
  showFavorites = false,
  showShare = false,
  availableDateMessage,
  modelHome,
  onOpenModal = () => {},
  onChange = () => {},
  handleFilterChange = () => {},
  filter,
  favoritesList,
  saveFavorite,
  deleteFavorite,
  title,
  messageFlag,
  filteredCtas,
  messageFlagLogo,
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const { ContextID = {} } = useSitecoreContext() || {};

  const id = ContextID.value;

  const [position, setPosition] = useState(0);
  const [isSharedVisible, setIsSharedVisible] = useState(false);
  const [itemDescription, setItemDescription] = useState('');

  const isIOS = getMobileOS() === 'iOS';
  const galleryContainerRef = useRef();

  const len = carousel.length;

  useEffect(() => {
    try {
      const posArray =
        position > -1 ? position % len : (len - (position % len) * -1) % len;
      const description =
        carousel && carousel[posArray] && carousel[posArray].description;
      setItemDescription(description);
      // console.log(position, posArray, description);
    } catch (e) {
      console.log(e);
    }
    onChange(position);
  }, [position, len, carousel]);

  /* const itemTypes = carousel.reduce((types, item) => {
    if (!types.includes(item.type)) {
      types.push(item.type);
    }
    return types;
  }, []);*/

  const isFavorite = favoritesList.includes(id);

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  const sliderSettings = {
    className: 'slider w-full h-full overflow-hidden',
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchThreshold: 100,
    // lazyLoad is intentionally off: react-slick only fills lazy slides after
    // mount, so SSR emitted empty slides. Media loading is deferred per element
    // instead (native loading='lazy' / preload='none' on non-initial slides).
    prevArrow: isMobile ? <></> : <CustomPrevArrow isMobile={isMobile} />,
    nextArrow: isMobile ? <></> : <CustomNextArrow isMobile={isMobile} />,
    afterChange: (current) => {
      setPosition(current);
      pauseVideos();
    },
  };

  return (
    <Container
      className={cx(
        'w-full h-full rounded-none md:rounded relative flex flex-col items-center overflow-auto',
        className,
      )}
      tabIndex={'0'}
      onKeyDown={(e) => {
        if (!e.shiftKey && e.key === 'Tab') {
          const current = galleryContainerRef.current;
          const focusElement = document.activeElement;
          if (current && current.isEqualNode(focusElement)) {
            setTimeout(() => {
              const filters = current.children[1];
              if (filters) {
                filters.children[0].focus();
                e.preventDefault();
              }
            }, 100);
          }
        }
      }}
      ref={galleryContainerRef}
      role='region'
      aria-label={'Images and videos of: ' + title}
    >
      <Slider {...sliderSettings}>
        {carousel &&
          carousel.map((item, index) => {
            const isVideo = ['internal', 'external'].includes(item.type);
            const itemVideoType = getVideoType(item.src);
            const carouselSrc =
              isVideo && itemVideoType === 'external'
                ? getEmbedUrl(item.src, false, true)
                : item.type === 'tour'
                ? getScrFromIframe(item.src)
                : item.src;
            const isValid = isValidUrl(carouselSrc);
            try {
              return (
                isValid && (
                  <div
                    className='h-full w-full justify-center text-center'
                    key={item.image ? item.image.src : item.id}
                  >
                    {item.type === 'image' && (
                      <div>
                        <ImgContainer
                          src={item.src}
                          alt={item.alt}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          className='h-full w-full md:min-w-full object-cover'
                          onClick={() => onOpenModal()}
                        />
                      </div>
                    )}
                    {isVideo && itemVideoType === 'external' && (
                      <IframeContainer className='videoIframe bg-light-blue w-full h-full px-10 md:px-20'>
                        <Iframe
                          src={getEmbedUrl(item.src, false, true)}
                          title={item.alt}
                          loading='lazy'
                        />
                      </IframeContainer>
                    )}
                    {isVideo && itemVideoType === 'internal' && (
                      <InternalVideoContainer
                        className={cx(
                          'w-full h-full bg-light-blue px-6 md:px-0',
                        )}
                      >
                        <InternalVideo
                          className='bg-light-blue'
                          src={item.src}
                          aria-label={item.alt}
                          controls
                          playsInline
                          preload={index === 0 ? 'metadata' : 'none'}
                          disablePictureInPicture='true'
                          controlsList='nodownload'
                          autoPlay={false}
                        />
                      </InternalVideoContainer>
                    )}
                    {item.type === 'tour' && (
                      <IframeContainer className='w-full h-full px-10 md:px-20'>
                        <Iframe
                          src={getScrFromIframe(item.src)}
                          title={item.description}
                          frameborder='0'
                          allowFullScreen='true'
                          webkitallowfullscreen='true'
                          mozallowfullscreen='true'
                          loading='lazy'
                        />
                      </IframeContainer>
                    )}
                  </div>
                )
              );
            } catch (e) {
              console.log(e);
            }
          })}
      </Slider>
      {filteredCtas && filteredCtas.length > 1 && (
        <FiltersContainer
          className={cx(
            'absolute top-0 left-0 ml-5 rounded-full flex cursor-pointer justify-center items-center',
            'bg-white text-mattamy-blue text-sm font-trade-gothic-20 uppercase',
            {
              'mt-12': isMobile && messageFlag,
              'mt-6': !(isMobile && messageFlag),
            },
          )}
          role='radiogroup'
          aria-label='Content type'
        >
          {filteredCtas &&
            filteredCtas.map((item, i) => (
              <>
                <FiltersMediaButton
                  className={cx(
                    'h-full flex flex-row  uppercase border-2 border-mattamy-blue items-center',
                    {
                      'pl-2 rounded-l-full': i === 0,
                      'pr-2 rounded-r-full': i === filteredCtas.length - 1,
                      'bg-mattamy-blue': isFilterSelected(item, filter),
                      'bg-white': !isFilterSelected(item, filter),
                    },
                  )}
                  isTour={item.text === '360 Tours'}
                  key={item.text}
                  onClick={(e) => {
                    handleFilterChange(item.text);
                  }}
                  role='radio'
                  aria-checked={isFilterSelected(item, filter)}
                  aria-label={'Select ' + item.text + 'filter'}
                >
                  <div
                    className={cx('pl-2 flex w-8', {
                      'text-mattamy-blue': !isFilterSelected(item, filter),
                      'text-white': isFilterSelected(item, filter),
                    })}
                  >
                    {item.text === 'Videos' && (
                      <VideoIconGallery className={'w-8'} />
                    )}
                    {item.text === '360 Tours' && (
                      <TourIconGallery className={'w-8'} />
                    )}
                    {item.text === 'Photos' && (
                      <PhotoIconGallery className={'w-8'} />
                    )}
                  </div>

                  <span
                    className={cx('ml-1 mt-1  pr-1', {
                      'text-mattamy-blue': !isFilterSelected(item, filter),
                      'text-white': isFilterSelected(item, filter),
                    })}
                  >
                    {item.text}
                  </span>
                </FiltersMediaButton>
              </>
            ))}
        </FiltersContainer>
      )}
      {!isMobile && messageFlag && (
        <div className='absolute top-0 left-0 flex flex-row w-full mt-16'>
          <QMIFlagMessageContainer className='flex items-center justify-center px-6 py-1 mt-10-mattamy md:mt-1 text-promo-dark-blue border-promo-dark-blue bg-aqua rounded-r'>
            <p className='px-4 text-center font-trade-gothic-20 uppercase text-lg tracking-wide leading-3 mt-1'>
              {messageFlag}
            </p>
          </QMIFlagMessageContainer>
        </div>
      )}
      {!isMobile && messageFlagLogo && (
        <MessageFlagLogoContainer
          className={cx('absolute top-0 left-0 flex flex-row w-full xl:mb-4', {
            'mt-5': !messageFlag,
            'mt-16': messageFlag,
          })}
        >
          <MessageFlagLogoImage
            className={cx('object-cover ml-5 mt-12', {})}
            src={messageFlagLogo.src}
            alt={
              messageFlagLogo.alt
                ? messageFlagLogo.alt
                : 'message flag logo image'
            }
          />
        </MessageFlagLogoContainer>
      )}
      {isMobile && messageFlag && (
        <AvailableFlag className='h-10 w-full absolute inset-x top-0 flex justify-center items-center text-promo-dark-blue bg-aqua border-promo-dark-blue border-2'>
          <p className='h-4 text-sm md:text-base font-trade-gothic-20 uppercase'>
            {messageFlag}
          </p>
        </AvailableFlag>
      )}
      {isMobile && messageFlagLogo && (
        <AvailableFlag
          className={cx(
            'h-10 w-full absolute top-0 left-0 flex flex-row w-full',
            {
              'mt-20': messageFlag,
              'mt-16': !messageFlag,
            },
          )}
        >
          <MessageFlagLogoImage
            className={cx('object-cover h-10 ml-5', {
              'mt-3': messageFlag,
              'mt-1': !messageFlag,
            })}
            src={messageFlagLogo.src}
            alt={
              messageFlagLogo.alt
                ? messageFlagLogo.alt
                : 'message flag logo image'
            }
          />
        </AvailableFlag>
      )}
      {(availableDateMessage || modelHome) && (
        <AvailableFlag
          className='h-10 w-full md:w-auto bg-mattamy-blue-light absolute inset-x bottom-0 md:top-0 md:right-0 md:px-10 flex
          justify-center items-center md:rounded-tr md:rounded-bl'
        >
          <p className='h-4 text-sm md:text-base text-white font-trade-gothic-20 uppercase'>
            {availableDateMessage || modelHome}
          </p>
        </AvailableFlag>
      )}
      <div
        className={cx(
          'absolute right-0 md:top-0 md:right-0 flex md:mt-5 mr-5 justify-center items-center flex flex-col',
          {
            'mt-12 md:mt-16': availableDateMessage || modelHome,
          },
        )}
      >
        <div className={cx('flex justify-between items-start pt-3 md:pt-0')}>
          {showFavorites && (
            <IconContainer
              className='flex items-center justify-center rounded-full bg-white text-mattamy-blue mr-10-mattamy hover:bg-mattamy-blue hover:text-white cursor-pointer'
              onClick={() => handleFavoriteClick(id)}
              aria-label={
                isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'
              }
              role='button'
              aria-pressed={isFavorite ? 'true' : 'false'}
            >
              {isFavorite ? (
                <HeartFilled className='h-5 w-5' />
              ) : (
                <HeartIcon className='h-5 w-5' />
              )}
            </IconContainer>
          )}
          {showShare && (
            <div
              onBlur={() => {
                setTimeout(() => {
                  setIsSharedVisible(false);
                }, 100);
              }}
            >
              <IconContainer
                className={cx(
                  'flex items-center justify-center rounded-full bg-white text-mattamy-blue hover:bg-mattamy-blue hover:text-white focus:outline-none cursor-pointer',
                  { 'bg-mattamy-blue text-white': isSharedVisible },
                )}
                // tabIndex="1"
                onClick={() => {
                  setIsSharedVisible(!isSharedVisible);
                }}
              >
                <ShareIcon
                  className={cx('h-5', {
                    'text-white': isSharedVisible,
                  })}
                />
              </IconContainer>
              {isSharedVisible && (
                <>
                  <div
                    className='st-custom-button'
                    onClick={() => {
                      shareThis({
                        dataNetwork: 'email',
                        dataUrl: getUrl(),
                      });
                    }}
                    aria-label={'Share by email'}
                  >
                    <IconContainer
                      data-aos='fade-down'
                      data-aos-duration='100'
                      className='rounded-full bg-mattamy-blue mt-2 flex items-center justify-center focus:outline-none'
                    >
                      <MailIcon className='h-5 w-5 text-white' />
                    </IconContainer>
                  </div>
                  {isMobile && (
                    <a
                      href={`sms:${isIOS ? '&' : '?'}body=${getUrl()}`}
                      onClick={() => {
                        track({ eventId: trackingEvents.ClickToText });
                        trackAnalyticsEvent({
                          gtagEvent: 'ShareClick',
                          mouseflowTag: 'Share',
                        });
                      }}
                      aria-label={'Share by text message'}
                    >
                      <IconContainer
                        data-aos='fade-down'
                        data-aos-duration='100'
                        data-aos-delay='50'
                        className='rounded-full bg-mattamy-blue mt-2 flex items-center justify-center focus:outline-none'
                      >
                        <ChatIcon className='h-5 w-5 text-white' />
                      </IconContainer>
                    </a>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {carousel.length > 1 && (
        <div
          className={cx(
            'bg-white absolute bottom-0 mr-5 rounded-full text-sm text-mattamy-blue px-3',
            'flex justify-center items-center font-trade-gothic-20',
            {
              'mb-6 ': !(isMobile && (availableDateMessage || modelHome)),
              'right-0': filteredCtas && filteredCtas.length,
              'mb-12': isMobile && (availableDateMessage || modelHome),
            },
          )}
        >
          <p className='mt-1'>
            {Math.sign(position) !== -1
              ? (position % len) + 1
              : ((len - Math.abs(position % len)) % len) + 1}
            /{len}
          </p>
        </div>
      )}
      {/* {itemDescription && (
        <div
          className={cx("absolute bottom-0 rigth-0 w-full px-4", {
            "mb-16": carousel.length > 1,
            "mb-6": carousel.length <= 1,
          })}
        >
          <p
            className={cx(
              "text-center text-lg font-graphie font-bold text-white"
            )}
          >
            {itemDescription}
          </p>
        </div>
      )} */}
    </Container>
  );
};

ExtendedGallery.propTypes = {
  carousel: PropTypes.array.isRequired,
  ctas: PropTypes.array.isRequired,
  className: PropTypes.string,
  showFavorites: PropTypes.bool,
  showShare: PropTypes.bool,
  availableDateMessage: PropTypes.string,
  modelHome: PropTypes.string,
  onChange: PropTypes.func,
  handleImageClick: PropTypes.func,
  handleFilterChange: PropTypes.func,
};

export default ExtendedGallery;
